import axios from 'axios';
import { Review, ReviewStats, SyncInfo } from '../types/Review';

// API URL should be set at build time via environment variable
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Debug logging
console.log('Review Service API Configuration:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  API_BASE_URL,
  mode: import.meta.env.MODE,
  allEnvVars: import.meta.env
});

// Cache management
let cachedReviews: Review[] | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 60000; // 1 minute cache

export const reviewService = {
  async getReviews(): Promise<Review[]> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (cachedReviews && (now - cacheTimestamp) < CACHE_DURATION) {
      console.log('Returning cached reviews');
      return cachedReviews;
    }
    
    console.log('Fetching all reviews...');
    let allReviews: any[] = [];
    let page = 1;
    let hasMore = true;
    
    // For smaller datasets (<1000), fetch all pages
    // For larger datasets, only fetch first batch and let pagination handle the rest
    const maxPagesToFetch = 10; // Max 1000 reviews (10 pages * 100)
    
    while (hasMore && page <= maxPagesToFetch) {
      try {
        const response = await axios.get(`${API_BASE_URL}/reviews`, {
          params: { page, limit: 100 } // Fetch 100 at a time for efficiency
        });
        
        const data = response.data;
        
        // Handle both old format (direct array) and new paginated format
        if (data.pagination) {
          // New paginated format
          allReviews = allReviews.concat(data.reviews || []);
          hasMore = data.pagination.hasMore;
          
          // For large datasets, stop after initial batch
          if (data.pagination.totalItems > 1000) {
            console.log(`Large dataset detected (${data.pagination.totalItems} reviews). Loading first 1000 for performance.`);
            console.log('Use pagination controls to navigate through all reviews.');
            hasMore = page < maxPagesToFetch && data.pagination.hasMore;
          }
          
          page++;
          console.log(`Fetched page ${data.pagination.page} of ${Math.min(data.pagination.totalPages, maxPagesToFetch)} (${allReviews.length} reviews loaded)`);
        } else {
          // Old format or single response
          allReviews = data.reviews || data || [];
          hasMore = false;
        }
      } catch (error) {
        console.error('Error fetching reviews page:', error);
        hasMore = false;
      }
    }
    
    console.log(`Total reviews fetched: ${allReviews.length}`);
    
    // Transform API response to match frontend types
    const transformedReviews = allReviews.map((review: any) => ({
      id: review.reviewId || review.id,
      timestamp: review.createdAt || review.reviewDate || review.timestamp,
      source: review.source,
      rating: review.originalRating || review.rating || 0,
      title: review.title,
      review: review.reviewBody || review.review || '',
      reviewer_name: review.reviewerName || review.reviewer_name,
      verified_purchase: review.verified_purchase,
      ai_summary: review.ai_summary,
      sentiment: review.sentiment?.toLowerCase() || review.overallSentiment?.toLowerCase(),
      key_themes: review.key_themes || [],
      highlights: review.highlights || review.nuggets || [],
      actionable_insights: review.actionable_insights || [],
      response_priority: review.response_priority,
      suggested_response: review.suggested_response,
      product_issues: review.product_issues || [],
      positive_aspects: review.positive_aspects || [],
      metadata: review.metadata || {},
      productTitle: review.productTitle || review.product_title || ''
    }));
    
    // Cache the results
    cachedReviews = transformedReviews;
    cacheTimestamp = now;
    console.log(`Cached ${transformedReviews.length} reviews`);
    
    return transformedReviews;
  },
  
  // Clear cache method
  clearCache() {
    cachedReviews = null;
    cacheTimestamp = 0;
    console.log('Review cache cleared');
  },

  async addReview(review: Partial<Review>): Promise<Review> {
    const response = await axios.post(`${API_BASE_URL}/reviews`, review);
    this.clearCache(); // Invalidate cache after adding
    return response.data;
  },

  async getStats(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/stats`);
    const data = response.data;
    
    // Return the raw stats data from the backend
    // The backend already provides all the fields we need
    return data;
  },

  async deleteReview(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/reviews/${id}`);
    this.clearCache(); // Invalidate cache after deleting
  },

  async updateReview(id: string, updates: Partial<Review>): Promise<Review> {
    const response = await axios.put(`${API_BASE_URL}/reviews/${id}`, updates);
    this.clearCache(); // Invalidate cache after updating
    return response.data;
  },

  async getSyncInfo(): Promise<SyncInfo> {
    const response = await axios.get(`${API_BASE_URL}/sync/status`);
    return response.data;
  },

  async triggerSync(params: { source: string; startDate?: string; endDate?: string }): Promise<void> {
    await axios.post(`${API_BASE_URL}/sync/trigger`, params);
    this.clearCache(); // Invalidate cache after sync
  },
};
